/**
 * Keyholder-side wellness-check entry point (delivery block, Phase 10).
 *
 * Shown on the Dashboard while viewing a primary user's data. Opens the
 * keyholder-initiated release protocol: FinalKey alerts the primary by
 * text and email, again after 7 silent days, and after 3 more releases
 * their LETTERS ONLY to the people they chose. Any alive signal cancels.
 *
 * The confirm dialog carries the whole weight of the action — this is a
 * button someone presses on a hard day, so the copy says exactly what
 * will and won't happen.
 */
import { useFocusEffect } from '@react-navigation/native';
import React, { useCallback, useState } from 'react';
import { View, StyleSheet, TouchableOpacity, Alert } from 'react-native';

import { wellnessService, WellnessRequesterStatus } from '../services/wellnessService';
import { createLogger } from '../utils/logger';
import { ms } from '../utils/responsive';

import { AppText as Text } from './AppText';

const logger = createLogger('WellnessRequestCard');

interface Props {
  primaryUserId: string;
  primaryName?: string;
}

export const WellnessRequestCard: React.FC<Props> = ({ primaryUserId, primaryName }) => {
  const [status, setStatus] = useState<WellnessRequesterStatus | null>(null);
  const [starting, setStarting] = useState(false);

  const name = primaryName || 'them';

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      wellnessService
        .getStatusFor(primaryUserId)
        .then((data) => {
          if (!cancelled) setStatus(data);
        })
        .catch((error) => {
          logger.debug('Wellness requester status unavailable:', error);
        });
      return () => {
        cancelled = true;
      };
    }, [primaryUserId]),
  );

  const handleStart = () => {
    Alert.alert(
      'Check on ' + name + '?',
      `We'll reach out to ${name} by text and email right away, and again after 7 days. ` +
        `If we can't reach them for 10 days, the letters they set up for delivery are ` +
        `released to the people they chose. Nothing else in their account is released this way.\n\n` +
        `If they respond at any point, the check ends quietly and you'll be told they're okay.`,
      [
        { text: 'Not now', style: 'cancel' },
        {
          text: 'Start the check',
          style: 'destructive',
          onPress: async () => {
            if (starting) return;
            try {
              setStarting(true);
              const result = await wellnessService.initiate(primaryUserId);
              setStatus({ active: true, mine: true, releaseDueAt: result.releaseDueAt });
              Alert.alert(
                'Wellness check started',
                "We've reached out. You'll get an email the moment anything changes.",
              );
            } catch (error: any) {
              logger.error('Failed to start wellness check:', error);
              Alert.alert(
                'Could not start',
                error?.response?.data?.message ||
                  error?.message ||
                  'Something went wrong. Please try again.',
              );
            } finally {
              setStarting(false);
            }
          },
        },
      ],
    );
  };

  if (status?.active) {
    const dueText = status.releaseDueAt
      ? new Date(status.releaseDueAt).toLocaleDateString()
      : null;
    return (
      <View style={styles.card} accessibilityRole="text">
        <Text style={styles.title}>Wellness check in progress</Text>
        <Text style={styles.body}>
          {status.mine ? "You've asked" : 'Someone has asked'} us to check on {name}.
          {dueText ? ` If we can't reach them by ${dueText}, their letters will be released.` : ''}{' '}
          You'll be emailed the moment anything changes.
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.card}>
      <Text style={styles.title}>Worried about {name}?</Text>
      <Text style={styles.body}>
        If you can't reach them, we can check on them for you — and deliver the letters they
        prepared if the worst has happened.
      </Text>
      <TouchableOpacity
        style={[styles.button, starting && styles.buttonDisabled]}
        onPress={handleStart}
        disabled={starting}
        accessibilityRole="button"
        accessibilityLabel={`Ask us to check on ${name}`}
      >
        <Text style={styles.buttonText}>Ask us to check on {name}</Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    marginHorizontal: ms(16),
    marginBottom: ms(12),
    padding: ms(16),
  },
  title: {
    fontSize: ms(15, 0.3),
    fontWeight: '700',
    color: '#1C2C4F',
  },
  body: {
    fontSize: ms(13, 0.3),
    lineHeight: ms(19, 0.3),
    color: '#6B7280',
    marginTop: ms(4),
  },
  button: {
    backgroundColor: '#1C2C4F',
    borderRadius: ms(25),
    paddingVertical: ms(11),
    alignItems: 'center',
    marginTop: ms(12),
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
});

export default WellnessRequestCard;
